'use strict';

/* jshint node: true, esnext: true, loopfunc: true, undef: true, unused: true */
/* global setImmediate, process, setTimeout */

var arrayUtils = require('zero-lang/array');
var objectUtils = require('zero-lang/object');
var checkType = require('zero-lang/type');
var declare = require('zero-oop/declare');

var Resolver = declare({
    constructor: function constructor() {
        objectUtils.extend(this, {
            _callbacks: [],
            _errbacks: [],
            _status: 'pending',
            _result: null
        });
    },
    fulfill: function fulfill(value) {
        var me = this;
        var status = me._status;

        if (status === 'pending' || status === 'accepted') {
            me._result = value;
            me._status = 'fulfilled';
        }

        if (me._status === 'fulfilled') {
            me._notify(me._callbacks, me._result);
            me._callbacks = [];
            me._errbacks = null;
        }
    },
    reject: function reject(reason) {
        var me = this;
        var status = me._status;

        if (status === 'pending' || status === 'accepted') {
            me._result = reason;
            me._status = 'rejected';
        }

        if (me._status === 'rejected') {
            me._notify(me._errbacks, me._result);
            me._callbacks = null;
            me._errbacks = [];
        }
    },
    resolve: function resolve(value) {
        var me = this;
        if (me._status === 'pending') {
            me._status = 'accepted';
            me._value = value;

            if (me._callbacks && me._callbacks.length || me._errbacks && me._errbacks.length) {
                me._unwrap(me._value);
            }
        }
    },
    _unwrap: function _unwrap(value) {
        var me = this;
        var unwrapped = false;
        var then = undefined;

        if (!value || !checkType.isObject(value) && !checkType.isFunction(value)) {
            me.fulfill(value);
            return;
        }

        try {
            then = value.then;
            if (checkType.isFunction(then)) {
                then.call(value, function (value) {
                    if (!unwrapped) {
                        unwrapped = true;
                        me._unwrap(value);
                    }
                }, function (reason) {
                    if (!unwrapped) {
                        unwrapped = true;
                        me.reject(reason);
                    }
                });
            } else {
                me.fulfill(value);
            }
        } catch (e) {
            if (!unwrapped) {
                me.reject(e);
            }
        }
    },
    _addCallbacks: function _addCallbacks(callback, errback) {
        var me = this;
        var callbackList = me._callbacks;
        var errbackList = me._errbacks;

        if (callbackList) {
            callbackList.push(callback);
        }
        if (errbackList) {
            errbackList.push(errback);
        }
        switch (me._status) {
            case 'accepted':
                me._unwrap(me._value);
                break;
            case 'fulfilled':
                me.fulfill(me._result);
                break;
            case 'rejected':
                me.reject(me._result);
                break;
        }
    },
    _notify: function _notify(subs, result) {
        if (subs.length) {
            Constructor.async(function () {
                arrayUtils.each(subs, function (sub) {
                    sub(result);
                });
            });
        }
    }
});

function Constructor(fn) {
    var me = this;
    if (!checkType.isFunction(fn)) {
        throw new TypeError('Promise resolver ' + fn + ' is not a function');
    }

    var resolver = me._resolver = new Resolver();

    try {
        fn(function (value) {
            resolver.resolve(value);
        }, function (reason) {
            resolver.reject(reason);
        });
    } catch (e) {
        resolver.reject(e);
    }
}

objectUtils.extend(Constructor, {
    Resolver: Resolver,
    resolve: function resolve(value) {
        if (value && value.constructor === this) {
            return value;
        }
        return new this(function (resolve) {
            resolve(value);
        });
    },
    reject: function reject(reason) {
        var promise = new this(function () {});

        promise._resolver._result = reason;
        promise._resolver._status = 'rejected';
        return promise;
    },
    all: function all(values) {
        return new Constructor(function (resolve, reject) {
            if (!checkType.isArray(values)) {
                reject(new Error('Promise.all expects an array of values or promises'));
                return;
            }

            var remaining = values.length;
            var i = 0;
            var length = values.length;
            var results = [];

            function oneDone(index) {
                return function (value) {
                    results[index] = value;

                    remaining--;

                    if (!remaining) {
                        resolve(results);
                    }
                };
            }
            if (length < 1) {
                return resolve(results);
            }
            for (; i < length; i++) {
                Constructor.resolve(values[i]).then(oneDone(i), reject);
            }
        });
    },
    race: function race(values) {
        return new Constructor(function (resolve, reject) {
            if (!checkType.isArray(values)) {
                reject(new Error('Promise.race expects an array of values or promises'));
                return;
            }
            arrayUtils.each(values, function (value) {
                Constructor.resolve(value).then(resolve, reject);
            });
        });
    },
    async: (function () {
        try {
            if (setImmediate) {
                return function (fn) {
                    setImmediate(fn);
                };
            }
        } catch (e) {}
        try {
            return process.nextTick;
        } catch (e) {}
        try {} catch (e) {
            return function (fn) {
                setTimeout(fn, 0);
            };
        }
        throw 'setTimeout not supported!';
    })(),
    _makeCallback: function _makeCallback(promise, resolve, reject, fn) {
        return function (valueOrReason) {
            var result;

            try {
                result = fn(valueOrReason);
            } catch (e) {
                reject(e);
                return;
            }
            if (result === promise) {
                // test suite promises-aplus-tests#2.3.1 requires a TypeError instance
                reject(new TypeError('Cannot resolve a promise with itself'));
                return;
            }
            resolve(result);
        };
    }
});

var PromiseShim = declare({
    constructor: Constructor,
    then: function then(callback, errback) {
        var resolve = undefined,
            reject = undefined;
        var promise = new Constructor(function (res, rej) {
            resolve = res;
            reject = rej;
        });

        this._resolver._addCallbacks(checkType.isFunction(callback) ? Constructor._makeCallback(promise, resolve, reject, callback) : resolve, checkType.isFunction(errback) ? Constructor._makeCallback(promise, resolve, reject, errback) : reject);
        return promise;
    },
    'catch': function _catch(errback) {
        return this.then(undefined, errback);
    }
});

// comment this code block to test {
if (typeof Promise !== 'undefined' && Promise && !!Promise.resolve) {
    PromiseShim = Promise;
}
// }

module.exports = PromiseShim;


/*****************
 ** WEBPACK FOOTER
 ** ./~/zero-net/~/zero-async/Promise.js
 ** module id = 2
 ** module chunks = 0 1
 **/